import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, MessageCircle, Twitter, Github, Send,
  CheckCircle, ArrowRight, HelpCircle, Zap,
  Building2, Shield, ChevronDown, ChevronUp,
} from 'lucide-react';
import PageLayout from '../components/PageLayout';

const CHANNELS = [
  {
    icon: Mail,
    title: 'Email Support',
    desc: 'For account issues, billing questions, and technical support.',
    value: 'support@zenithcipher.io',
    action: 'mailto:support@zenithcipher.io',
    badge: 'Responds in < 24h',
    accent: '#00E5FF',
  },
  {
    icon: MessageCircle,
    title: 'Discord Community',
    desc: 'Join our community for real-time help, API questions, and announcements.',
    value: 'discord.gg/zenithcipher',
    action: 'https://discord.com',
    badge: 'Active daily',
    accent: '#7C3AED',
  },
  {
    icon: Twitter,
    title: 'Twitter / X',
    desc: 'Follow us for product updates, security research posts, and Web3 news.',
    value: '@ZenithCipher',
    action: 'https://twitter.com',
    badge: 'We reply fast',
    accent: '#1d9bf0',
  },
  {
    icon: Building2,
    title: 'Enterprise & Sales',
    desc: 'Custom plans, integrations, and SLA discussions for larger teams.',
    value: 'sales@zenithcipher.io',
    action: 'mailto:sales@zenithcipher.io',
    badge: 'Priority response',
    accent: '#f59e0b',
  },
];

const FAQS = [
  {
    q: 'How do I get my API key?',
    a: 'After creating a free account, navigate to Dashboard → API Access. You can generate, view, copy, and revoke API keys there. Free accounts get 1 key, Starter gets 3, Pro gets 10.',
  },
  {
    q: 'Why is my wallet scan showing an error?',
    a: 'Make sure the address is a valid Ethereum address starting with 0x, exactly 42 characters. Errors can also occur if the Etherscan API is temporarily unavailable — try again in a few seconds.',
  },
  {
    q: 'Do you support chains other than Ethereum?',
    a: 'Currently ZenithCipher supports Ethereum mainnet. We\'re actively working on support for Base, Polygon, Arbitrum, and BSC. Join our Discord or follow us on Twitter for launch announcements.',
  },
  {
    q: 'How does billing work? Can I cancel anytime?',
    a: 'We bill monthly. You can upgrade, downgrade, or cancel anytime from Dashboard → Billing. If you cancel, you keep access until the end of the billing period. No cancellation fees.',
  },
  {
    q: 'Is my scan data private? Who can see it?',
    a: 'Your scan history is private and visible only to you. We do not share individual scan data with third parties. Wallet addresses are public on-chain data — we only store the analysis results.',
  },
  {
    q: 'Do you offer refunds?',
    a: "If you're not satisfied within the first 14 days of a paid plan, contact us at support@zenithcipher.io and we'll issue a full refund. No questions asked.",
  },
  {
    q: 'Can I use ZenithCipher for compliance and AML reporting?',
    a: 'Yes. Our Pro and Enterprise plans include export features that generate audit-ready JSON reports. Enterprise customers can also get custom compliance integrations. Contact sales for details.',
  },
  {
    q: 'How accurate is the risk scoring?',
    a: 'Our Risk Engine v2.0 is calibrated to minimize false positives while catching genuine risk signals. Risk scores are advisory — not definitive judgments. Always use them alongside your own due diligence.',
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.05] last:border-0">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full text-left flex items-start justify-between gap-4 py-5 group"
      >
        <span className="text-sm font-semibold text-white group-hover:text-[#00E5FF] transition-colors leading-snug">
          {q}
        </span>
        <span className="flex-shrink-0 text-slate-500 mt-0.5">
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </span>
      </button>
      {open && (
        <div className="pb-5 text-sm text-slate-400 leading-relaxed -mt-2">
          {a}
        </div>
      )}
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: 'general', message: '' });
  const [sent, setSent]   = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    // Simulate a short delay (replace with real API call)
    await new Promise(r => setTimeout(r, 1200));
    setLoading(false);
    setSent(true);
  };

  return (
    <PageLayout
      badge="Contact Us"
      badgeColor="cyan"
      title={<>We'd love to<br /><span className="text-[#00E5FF]">hear from you</span></>}
      subtitle="Have a question about the API, need help with your account, or want to discuss an enterprise plan? We're here."
    >
      {/* Support channels */}
      <section className="py-16 bg-[#080c16]/60 border-y border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-3 py-1.5 rounded-full mb-3">
              <Zap size={11} /> Multiple ways to reach us
            </div>
            <h2 className="text-2xl font-bold text-white">Choose your preferred channel</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CHANNELS.map(({ icon: Icon, title, desc, value, action, badge, accent }) => (
              <a
                key={title}
                href={action}
                target={action.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="glass-card border border-white/[0.06] p-5 group hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                  style={{ background: `${accent}15`, border: `1px solid ${accent}25` }}
                >
                  <Icon size={18} style={{ color: accent }} />
                </div>
                <div className="text-sm font-bold text-white mb-1">{title}</div>
                <div className="text-xs text-slate-500 leading-relaxed mb-4 flex-1">{desc}</div>
                <div
                  className="text-xs font-semibold font-mono mb-3 truncate"
                  style={{ color: accent }}
                >
                  {value}
                </div>
                <span
                  className="text-[10px] font-bold px-2.5 py-1 rounded-full self-start"
                  style={{ background: `${accent}12`, color: accent }}
                >
                  {badge}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form + FAQ */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14">

            {/* Form */}
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#7C3AED] bg-[#7C3AED]/10 border border-[#7C3AED]/20 px-3 py-1.5 rounded-full mb-5">
                <Send size={11} /> Send a Message
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Drop us a line</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                Fill out the form below and we'll get back to you within one business day.
              </p>

              {sent ? (
                <div className="glass-card border border-green-500/20 bg-green-500/5 p-10 text-center rounded-2xl">
                  <CheckCircle size={36} className="text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">Message sent!</h3>
                  <p className="text-slate-400 text-sm mb-5">
                    Thanks for reaching out. We'll reply to <span className="text-white font-medium">{form.email}</span> within 24 hours.
                  </p>
                  <button
                    onClick={() => { setSent(false); setForm({ name: '', email: '', subject: 'general', message: '' }); }}
                    className="text-sm text-[#00E5FF] hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Full Name</label>
                      <input
                        required
                        type="text"
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Alex Johnson"
                        className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-[#00E5FF]/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Email Address</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="you@example.com"
                        className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-[#00E5FF]/40 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1.5">Subject</label>
                    <select
                      value={form.subject}
                      onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-slate-200 outline-none focus:border-[#00E5FF]/40 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="general"    className="bg-[#0B0F19]">General Inquiry</option>
                      <option value="support"    className="bg-[#0B0F19]">Technical Support</option>
                      <option value="billing"    className="bg-[#0B0F19]">Billing & Plans</option>
                      <option value="enterprise" className="bg-[#0B0F19]">Enterprise / Sales</option>
                      <option value="security"   className="bg-[#0B0F19]">Security Disclosure</option>
                      <option value="other"      className="bg-[#0B0F19]">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1.5">Message</label>
                    <textarea
                      required
                      rows={6}
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Describe your question or issue in detail…"
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-[#00E5FF]/40 transition-colors resize-none"
                    />
                  </div>

                  {form.subject === 'security' && (
                    <div className="flex gap-3 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 text-xs text-amber-400">
                      <Shield size={14} className="flex-shrink-0 mt-0.5" />
                      For responsible security disclosures, you can also email us directly at{' '}
                      <a href="mailto:security@zenithcipher.io" className="underline">security@zenithcipher.io</a>.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] disabled:opacity-50 text-black font-bold py-3.5 rounded-xl transition-all text-sm shadow-lg shadow-[#00E5FF]/20"
                  >
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send size={14} /> Send Message
                      </>
                    )}
                  </button>
                  <p className="text-xs text-slate-600 text-center">
                    We typically respond within 1 business day. For urgent issues, use Discord.
                  </p>
                </form>
              )}
            </div>

            {/* FAQ */}
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-3 py-1.5 rounded-full mb-5">
                <HelpCircle size={11} /> Frequently Asked
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Common questions</h2>
              <p className="text-slate-400 text-sm mb-8">
                Quick answers to the questions we hear most often.
              </p>
              <div className="glass-card border border-white/[0.06] px-6 divide-y divide-white/[0.05] rounded-2xl">
                {FAQS.map(faq => <FAQItem key={faq.q} {...faq} />)}
              </div>
              <p className="text-slate-500 text-xs mt-5 text-center">
                Still stuck?{' '}
                <Link to="/docs" className="text-[#00E5FF] hover:underline">Check the full docs →</Link>
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* GitHub / open source note */}
      <section className="py-14 bg-[#080c16]/60 border-t border-white/[0.05]">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <Github size={24} className="text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-white mb-2">Found a bug? Open an issue.</h3>
          <p className="text-slate-500 text-sm mb-5">
            Our dashboard and API client examples are open source. Found a bug or want to contribute?
            We welcome issues and pull requests on GitHub.
          </p>
          <a
            href="https://github.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-white/10 hover:border-white/20 bg-white/[0.04] text-slate-300 font-medium px-5 py-2.5 rounded-xl transition-all text-sm"
          >
            <Github size={14} /> View on GitHub <ArrowRight size={12} />
          </a>
        </div>
      </section>
    </PageLayout>
  );
}
